import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Brain, 
  ExternalLink, 
  ArrowRight, 
  Plus,
  Calendar,
  Target,
  Clock,
  CheckCircle,
  Play,
  Users
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSkillsAssessments } from '../../hooks/useSkillsAssessments';

interface RecentSkillsAssessmentsProps {
  onNavigateToSkillsAssessments?: () => void;
  limit?: number;
  projectId?: string | null;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'active':
      return <Play className="h-4 w-4 text-blue-600" />;
    case 'draft':
      return <Clock className="h-4 w-4 text-yellow-600" />;
    default:
      return <Target className="h-4 w-4 text-slate-600" />;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'active':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'draft':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

export function RecentSkillsAssessments({ 
  onNavigateToSkillsAssessments, 
  limit = 5,
  projectId = null 
}: RecentSkillsAssessmentsProps) {
  const { assessments, isLoading } = useSkillsAssessments();
  const navigate = useNavigate();

  // Filter assessments by project if needed
  const filteredAssessments = useMemo(() => {
    if (projectId) {
      return assessments.filter(assessment => 
        (assessment as any).project_id === projectId || (assessment as any).projectId === projectId
      );
    }
    return assessments;
  }, [assessments, projectId]);

  // Filter and sort recent assessments
  const recentAssessments = useMemo(() => {
    return filteredAssessments
      .sort((a, b) => new Date((b as any).created_at || b.createdAt || 0).getTime() - new Date((a as any).created_at || a.createdAt || 0).getTime())
      .slice(0, limit);
  }, [filteredAssessments, limit]);

  // Helper function to handle navigation
  const handleNavigateToSkillsAssessments = () => {
    if (onNavigateToSkillsAssessments) {
      onNavigateToSkillsAssessments();
    } else {
      navigate('/?tab=skills-assessment');
    }
  };

  // Calculate activity stats
  const activityStats = useMemo(() => {
    const totalAssessments = filteredAssessments.length;
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentAssessments = filteredAssessments.filter(assessment => 
      new Date((assessment as any).created_at || assessment.createdAt || 0) > oneWeekAgo
    ).length;

    return {
      totalAssessments,
      recentAssessments
    };
  }, [filteredAssessments]);

  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-4 pt-2 min-h-[200px]">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty State
  if (recentAssessments.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8 flex flex-col items-center min-h-[200px] justify-center space-y-4">
        <Brain className="w-12 h-12 text-slate-400" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-600">
            No skills assessments yet
          </p>
          <p className="text-xs text-slate-500">
            Create custom skills assessments to evaluate candidates' technical abilities effectively.
          </p>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleNavigateToSkillsAssessments}
          className="mt-4 border-slate-200 hover:bg-slate-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Assessment
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Activity Statistics */}
      {activityStats.totalAssessments > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.recentAssessments}</div>
            <div className="text-xs text-slate-600 font-medium">This Week</div>
          </div>
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.totalAssessments}</div>
            <div className="text-xs text-slate-600 font-medium">Total</div>
          </div>
        </div>
      )}

      {/* Assessment Items */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {recentAssessments.map((assessment) => (
          <div 
            key={assessment.id} 
            className="flex items-center space-x-4 p-4 hover:bg-slate-50 rounded-xl transition-all duration-200 cursor-pointer group border border-transparent hover:border-slate-200"
            onClick={handleNavigateToSkillsAssessments}
          >
            <div className={cn(
              "w-12 h-12 flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-200 border",
              getStatusColor((assessment as any).status || 'draft')
            )}>
              {getStatusIcon((assessment as any).status || 'draft')}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-slate-700 transition-colors">
                {(assessment as any).title || assessment.name}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  {(assessment as any).skill_type || (assessment as any).category || 'General'}
                </span>
                {(assessment as any).questions_count && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {(assessment as any).questions_count} questions
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatTimeAgo((assessment as any).created_at || assessment.createdAt)}
                </span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </div>
        ))}
      </div>

      {/* View All Button */}
      {recentAssessments.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateToSkillsAssessments}
            className="w-full justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
          >
            View All Assessments
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
} 